// JavaScript version of mediaIssues.ts
// Provides MediaIssueInspector to evaluate WebRTC media health per peer.

/**
 * @typedef {'connecting'|'healthy'|'degraded'|'failed'} PeerMediaStatus
 */

// Keep constants in milliseconds
const GRACE_MS = 8000;
const FAIL_MS = 15000;
const DEGRADE_MS = 10000;
const STALL_THRESHOLD_INTERVALS = 2; // how many polls with no increment => stall

export class MediaIssueInspector {
  constructor(nowFn = () => Date.now()) {
    this.nowFn = nowFn;
    /** @type {Map<string, any>} */
    this.issues = new Map();
    /** @type {Map<string, any[]>} */
    this.lastPacketSnapshots = new Map();
  }

  /**
   * Update inspector with latest stats for a peer.
   * @param {string} peerId
   * @param {boolean} isLocal
   * @param {{
   *  audioOutboundPackets?: number,
   *  audioInboundPackets?: number,
   *  videoOutboundPackets?: number,
   *  videoInboundPackets?: number,
   *  audioRemoteRtpId?: string|null,
   *  videoRemoteRtpId?: string|null,
   *  transportReady?: boolean,
   * }} sample
   */
  update(peerId, isLocal, sample) {
    const now = this.nowFn();
    let issue = this.issues.get(peerId);
    if (!issue) {
      issue = {
        peerId,
        isLocal,
        status: 'connecting',
        firstObserved: now,
        lastUpdated: now,
        reasons: [],
        metrics: {}
      };
      this.issues.set(peerId, issue);
    }

    issue.metrics = {
      audioOutboundPackets: sample.audioOutboundPackets,
      audioInboundPackets: sample.audioInboundPackets,
      videoOutboundPackets: sample.videoOutboundPackets,
      videoInboundPackets: sample.videoInboundPackets,
      audioRemoteRtpId: sample.audioRemoteRtpId ?? null,
      videoRemoteRtpId: sample.videoRemoteRtpId ?? null,
      transportReady: !!sample.transportReady,
    };
    issue.lastUpdated = now;

    // Record packet history (retain last 3)
    const history = this.lastPacketSnapshots.get(peerId) || [];
    history.push(sample);
    if (history.length > 3) history.shift();
    this.lastPacketSnapshots.set(peerId, history);

    const elapsed = now - issue.firstObserved;
    const reasons = [];

    // Local outbound checks
    if (isLocal) {
      // Audio outbound
      if (sample.transportReady) {
        if (!sample.audioRemoteRtpId && (sample.audioOutboundPackets ?? 0) === 0) {
          if (elapsed > GRACE_MS) reasons.push('audio-outbound-no-rtp-association');
        } else if ((sample.audioOutboundPackets ?? 0) === 0 && elapsed > DEGRADE_MS) {
          reasons.push('audio-outbound-no-packets');
        }
      }
      // Video outbound
      if (sample.transportReady) {
        if (!sample.videoRemoteRtpId && (sample.videoOutboundPackets ?? 0) === 0) {
          if (elapsed > GRACE_MS) reasons.push('video-outbound-no-rtp-association');
        }
      }
    } else {
      // Remote inbound stall detection
      if (this.isStalled(history, 'audioInboundPackets')) {
        reasons.push('audio-inbound-stalled');
      }
      if (this.isStalled(history, 'videoInboundPackets')) {
        reasons.push('video-inbound-stalled');
      }
    }

    // Decide status
    if (elapsed < GRACE_MS) {
      issue.status = 'connecting';
    } else {
      if (reasons.length === 0) {
        issue.status = 'healthy';
      } else {
        const hardFailure = reasons.some(r =>
          r.includes('no-rtp-association') ||
          (elapsed > FAIL_MS && r.includes('no-packets'))
        );
        issue.status = hardFailure ? 'failed' : 'degraded';
      }
    }

    issue.reasons = reasons;
  }

  /**
   * @param {Array<Object>} history
   * @param {'audioInboundPackets'|'videoInboundPackets'} field
   */
  isStalled(history, field) {
    if (history.length < STALL_THRESHOLD_INTERVALS + 1) return false;
    // Compare oldest vs newest in the retained window
    const values = history.map(h => (h[field] ?? 0));
    const first = values[0];
    const last = values[values.length - 1];
    return last === first && last !== 0; // stuck (no increment) despite having some packets already
  }

  /**
   * @returns {Array<any>}
   */
  all() {
    return Array.from(this.issues.values());
  }
}
